import { onFCP, onINP, onTTFB, onLCP, onCLS } from "web-vitals";
import { metrics } from "@opentelemetry/api";
import { initializeOpenTelemetry } from "./instrumentation";

let metricsInitialized = false;
let webVitalsMeter;
let lcpHistogram;
let clsHistogram;
let inpHistogram;
let ttfbHistogram;
let fcpHistogram;

const SIGNOZ_URL = import.meta.env.VITE_SIGNOZ_URL;
const SIGNOZ_INGESTION_KEY = import.meta.env.VITE_SIGNOZ_INGESTION_KEY;

/**
 * Initialize OpenTelemetry metrics and create metric instruments
 */
function ensureMetricsInitialized() {
  if (metricsInitialized) {
    return;
  }

  if (!SIGNOZ_URL || !SIGNOZ_INGESTION_KEY) {
    console.warn(
      "SigNoz configuration missing. Set VITE_SIGNOZ_URL and VITE_SIGNOZ_INGESTION_KEY to enable web vitals tracking.",
      { SIGNOZ_URL, SIGNOZ_INGESTION_KEY },
    );
    return;
  }

  try {
    initializeOpenTelemetry({
      url: SIGNOZ_URL,
      ingestionKey: SIGNOZ_INGESTION_KEY,
      environment: import.meta.env.MODE || "production",
    });

    // Get the meter for web vitals
    webVitalsMeter = metrics.getMeter("web-vitals");

    // Create histogram instruments for each web vital
    lcpHistogram = webVitalsMeter.createHistogram("lcp", {
      description: "Largest Contentful Paint",
      unit: "ms",
    });

    clsHistogram = webVitalsMeter.createHistogram("cls", {
      description: "Cumulative Layout Shift",
      unit: "1",
    });

    inpHistogram = webVitalsMeter.createHistogram("inp", {
      description: "Interaction to Next Paint",
      unit: "ms",
    });

    ttfbHistogram = webVitalsMeter.createHistogram("ttfb", {
      description: "Time to First Byte",
      unit: "ms",
    });

    fcpHistogram = webVitalsMeter.createHistogram("fcp", {
      description: "First Contentful Paint",
      unit: "ms",
    });

    metricsInitialized = true;
  } catch (error) {
    console.error("Failed to initialize OpenTelemetry:", error);
  }
}

/**
 * Get account-level attributes to include with each metric
 * @returns {Object} Account attributes
 */
function getAccountAttributes() {
  const account = window.currentAccount;

  return {
    "account.public_id": account?.public_id || "unknown",
    "account.name": account?.name || "unknown",
    "account.is_test": account?.is_test_account || false,
  };
}

/**
 * Record a web vital metric with account-level context
 * @param {import('web-vitals').Metric} metric - Web vital metric
 * @param {Object} histogram - The histogram instrument to record to
 */
function recordWebVital(metric, histogram) {
  if (!metricsInitialized || !histogram) {
    return;
  }

  try {
    const attributes = {
      rating: metric.rating,
      navigation_type: metric.navigationType,
      "url.path": window.location.pathname,
      "url.query": window.location.search,
      ...getAccountAttributes(),
    };

    histogram.record(metric.value, attributes);
  } catch (error) {
    console.error("Failed to record web vital:", error);
  }
}

/**
 * Initialize web vitals tracking
 * Call this once when your application loads
 */
export function trackWebVitals() {
  ensureMetricsInitialized();

  if (!metricsInitialized) {
    console.warn("Web vitals tracking not initialized - skipping");
    return;
  }

  // Track all core web vitals as metrics
  onCLS((metric) => recordWebVital(metric, clsHistogram));
  onFCP((metric) => recordWebVital(metric, fcpHistogram));
  onINP((metric) => recordWebVital(metric, inpHistogram));
  onLCP((metric) => recordWebVital(metric, lcpHistogram));
  onTTFB((metric) => recordWebVital(metric, ttfbHistogram));
}
